<div class="manage-contribution-container">
  
  <!-- Loading overlay - blocks interactions while API is pending -->
  <div *ngIf="loading" class="manage-contribution-loading-overlay" aria-hidden="true"></div>

  <!-- Filter Section -->
  <app-filter-panel
    [fields]="filterFields"
    [defaultExpanded]="false"
    (filterChange)="onFilterChange($event)"
    (clearFilters)="onClearFilters()"
  ></app-filter-panel>

  <!-- Table -->
  <app-table
    [columns]="columns"
    [data]="contributionsData"
    [scrollHeight]="'400px'"
    [loading]="loading"
    [pagination]="pagination"
    [emptyMessage]="emptyMessage"
    [striped]="true"
    [bordered]="true"
    [hoverable]="true"
    (pageChange)="onPageChange($event)"
    (pageSizeChange)="onPageSizeChange($event)"
    (sortChange)="onSortChange($event)"
  >
    <!-- Custom Publish Status Column: Pending / Approved / Visible / Hidden / Rejected -->
    <ng-template #statusCell let-row>
      <div class="publish-status-cell">
        <span [ngClass]="getStatusIndicatorClass(row.status)"></span>
        <span class="publish-status-label">{{ getPublishStatusDisplayLabel(row.status) }}</span>
        <span
          *ngIf="isOrgAdminAlreadyRejected(row)"
          class="asset-status-info-icon asset-status-info-icon--rejected"
          [attr.title]="getOrgAdminRejectedTooltip(row)"
          [attr.aria-label]="getOrgAdminRejectedTooltip(row)"
          role="img"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.75"/>
            <path d="M12 10v6M12 7h.01" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/>
          </svg>
        </span>
      </div>
    </ng-template>

    <!-- Custom Permission Column -->
    <ng-template #permissionCell let-row>
      <div [ngClass]="getPermissionBadgeClass(row.permission)">
        <img src="/public.svg" alt="Permission" class="permission-badge-icon" *ngIf="row.permission && (row.permission.toUpperCase() === 'PUBLIC' || row.permission.toUpperCase() === 'OPEN' || row.permission.toUpperCase() === 'RESTRICTED')"/>
        <img src="/filters/lock.svg" alt="Private" class="permission-badge-icon" *ngIf="row.permission && row.permission.toUpperCase() === 'PRIVATE'"/>
        <span>{{ getPermissionLabel(row.permission) }}</span>
      </div>
    </ng-template>

    <!-- Custom Action Column (dropdown rendered in fixed overlay below) -->
    <ng-template #actionCell let-row>
      <div class="action-cell-wrapper">
        <button 
          class="action-btn" 
          appActionDropdownTrigger
          (click)="toggleActionDropdown($event, row)"
          [class.active]="isDropdownOpen(row)"
        >
          <span>Action</span>
        </button>
      </div>
    </ng-template>
  </app-table>
</div>

<!-- Fixed overlay: dropdown on top of table -->
<div
  *ngIf="overlayPosition && openDropdownRow"
  class="action-dropdown-overlay"
  role="menu"
  aria-label="Asset actions"
  [class.action-dropdown-overlay--above]="overlayPosition.openAbove"
  [style.top.px]="overlayPosition.top"
  [style.left.px]="overlayPosition.left"
  [style.right.px]="overlayPosition.right"
  (click)="$event.stopPropagation()"
>
  <ul class="dropdown-list">
    <li class="dropdown-item">
      <button type="button" role="menuitem" class="dropdown-link" (click)="handleAction('view', openDropdownRow)">View</button>
    </li>
    <ng-container *ngIf="actionContext === 'provider'">
      <li class="dropdown-item">
        <button type="button" role="menuitem" class="dropdown-link" (click)="handleAction('modifyMetadata', openDropdownRow)">Modify Metadata</button>
      </li>
      <li class="dropdown-divider" aria-hidden="true"></li>
      <li class="dropdown-item">
        <button type="button" role="menuitem" class="dropdown-link dropdown-link--danger" (click)="handleAction('delete', openDropdownRow)">Delete Asset</button>
      </li>
    </ng-container>
    <ng-container *ngIf="actionContext !== 'provider'">
      <li class="dropdown-item" *ngIf="canShowApproveAction(openDropdownRow)">
        <button
          type="button"
          role="menuitem"
          class="dropdown-link"
          (click)="handleAction('approve', openDropdownRow)"
        >
          Approve
        </button>
      </li>
      <li class="dropdown-item" *ngIf="canShowRejectAction(openDropdownRow)">
        <button
          type="button"
          role="menuitem"
          class="dropdown-link dropdown-link--danger"
          (click)="handleAction('reject', openDropdownRow)"
        >
          Reject
        </button>
      </li>
      <li class="dropdown-item" *ngIf="canShowHideAction(openDropdownRow)">
        <button
          type="button"
          role="menuitem"
          class="dropdown-link"
          (click)="handleAction('hide', openDropdownRow)"
        >
          Hide
        </button>
      </li>
      <li class="dropdown-item" *ngIf="canShowUnhideAction(openDropdownRow)">
        <button
          type="button"
          role="menuitem"
          class="dropdown-link"
          (click)="handleAction('unhide', openDropdownRow)"
        >
          Unhide
        </button>
      </li>
      <li class="dropdown-divider" aria-hidden="true"></li>
      <li class="dropdown-item">
        <button type="button" role="menuitem" class="dropdown-link dropdown-link--danger" (click)="handleAction('delete', openDropdownRow)">Delete</button>
      </li>
    </ng-container>
  </ul>
</div>

<!-- Delete Confirmation Modal -->
<app-confirmation-dialog
  [(visible)]="showDeleteConfirmModal"
  type="delete"
  themeColor="red"
  title="Delete Asset"
  [message]="rowToDelete ? 'Are you sure you want to delete &quot;' + (rowToDelete.assetName || 'this asset') + '&quot;? This action cannot be undone.' : ''"
  confirmLabel="Delete"
  cancelLabel="Cancel"
  [loading]="isDeleting"
  (onConfirm)="onConfirmDelete()"
  (onCancel)="onCancelDelete()"
></app-confirmation-dialog>

<!-- Modify Metadata Modal (provider context) -->
<app-modal
  [(visible)]="showModifyModal"
  header="Modify Asset Metadata"
  [width]="'900px'"
  [showFooter]="false"
  (onCancel)="closeModifyModal()"
>
  <div *ngIf="isLoadingAssetData" class="modify-metadata-loading">
    <p>Loading asset data...</p>
  </div>
  <app-provider-preview-metadata
    *ngIf="!isLoadingAssetData && modifyAssetData && modifyFormInstanceKey"
    [editableMode]="true"
    [allowSaveWithoutDirty]="true"
    [assetData]="modifyAssetData"
    [assetType]="getAssetTypeForModify()"
    (formSubmit)="onModifyFormSubmit($event)"
    (formCancel)="onModifyFormCancel()"
  ></app-provider-preview-metadata>
</app-modal>
